import type { Auth } from 'firebase/auth'
import { firebaseConfigured, runtimeConfig } from '@/config/runtime'

/**
 * Firebase Auth, loaded lazily (modular imports, auth only) so demo builds,
 * tests and the first paint never pay for it.
 *
 * Google popup and email/password both end in a Firebase ID token, which is
 * exchanged for a MafiaGo web session by WebSession.SignIn. Firebase keeps
 * its own browser session (IndexedDB), which is what lets a reload restore
 * silently while OUR tokens stay in memory.
 */
let authPromise: Promise<Auth> | null = null

export class FirebaseNotConfiguredError extends Error {
  readonly code = 'auth/not-configured'
}

export function getFirebaseAuth(): Promise<Auth> {
  if (!firebaseConfigured()) return Promise.reject(new FirebaseNotConfiguredError('firebase web config missing'))
  if (!authPromise) {
    authPromise = (async () => {
      const [{ initializeApp, getApps }, { getAuth }] = await Promise.all([
        import('firebase/app'),
        import('firebase/auth'),
      ])
      const app = getApps()[0] ?? initializeApp(runtimeConfig().firebase)
      return getAuth(app)
    })()
  }
  return authPromise
}

/** Resolves once Firebase has restored (or failed to restore) its session. */
export async function restoredIdToken(): Promise<string | null> {
  const auth = await getFirebaseAuth()
  await auth.authStateReady()
  return auth.currentUser ? auth.currentUser.getIdToken() : null
}

export async function googlePopupIdToken(): Promise<string> {
  const auth = await getFirebaseAuth()
  const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth')
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  const cred = await signInWithPopup(auth, provider)
  return cred.user.getIdToken()
}

export async function emailPasswordIdToken(email: string, password: string): Promise<string> {
  const auth = await getFirebaseAuth()
  const { signInWithEmailAndPassword } = await import('firebase/auth')
  const cred = await signInWithEmailAndPassword(auth, email, password)
  return cred.user.getIdToken()
}

/** A FORCED fresh ID token for the current Firebase user, or null if signed out. */
export async function freshIdToken(): Promise<string | null> {
  if (!firebaseConfigured()) return null
  const auth = await getFirebaseAuth()
  await auth.authStateReady()
  return auth.currentUser ? auth.currentUser.getIdToken(true) : null
}

export async function firebaseSignOut(): Promise<void> {
  if (!firebaseConfigured() || !authPromise) return
  const auth = await authPromise
  const { signOut } = await import('firebase/auth')
  await signOut(auth)
}

export async function firebaseEmail(): Promise<string | null> {
  if (!firebaseConfigured() || !authPromise) return null
  const auth = await authPromise
  return auth.currentUser?.email ?? null
}
